import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { gameContextSelector } from '@/features/game-context/store/game-context';
import { Provider } from '@/features/game-context/types';
import { init } from '@/features/game-window-integration/helpers/GCM';
import { resetGcmState } from '@/features/game-window-integration/helpers/GCM/state';
import { GcmObject } from '@/features/game-window-integration/helpers/GCM/types';

export const useLnwEventHandlers = () => {
  const { initialized, provider, region, gameUid } =
    useSelector(gameContextSelector);
  const gcmRef = useRef<GcmObject | null>(null);

  // Reset GCM state when game context is reset (e.g. soft reload after error)
  useEffect(() => {
    if (!initialized && gcmRef.current) {
      resetGcmState();
      gcmRef.current = null;
    }
  }, [initialized]);

  useEffect(() => {
    if (!initialized || !region || gcmRef.current || provider !== Provider.LNW)
      return;

    (async () => {
      const gcm = await init(region, gameUid);
      if (gcm) gcmRef.current = gcm;
    })();
  }, [initialized, provider, region, gameUid]);

  const wrapperEventHandler = useCallback((event: MessageEvent) => {
    const gcm = gcmRef.current;
    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
      gcm?.toggleMute(event.data.payload);
    }

    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
      if (event.data.payload) {
        gcm?.updateBalance(event.data.payload);
      }
    }

    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR) {
      gcm?.handleRecoverableErrorCallback();
    }

    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP) {
      gcm?.toggleHowToPlay();
    }

    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
      gcm?.sendStopAutoplay();
    }

    if (event.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
      gcm?.togglePaytable();
    }
  }, []);

  const providerEventHandler: () => void = () => {};

  return { wrapperEventHandler, providerEventHandler };
};
